import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { useApp } from "../context/AppContext";
import { ResolveIssueDialog } from "./ResolveIssueDialog";
import { Button } from "./ui/button";
import { AlertTriangle, X, MessageSquare, CheckCircle2, MapPin, ClipboardList, Clock } from "lucide-react";
import { toast } from "sonner";

/**
 * Floating alert (chat-head):
 * A pulsing button that floats above EVERY screen of the app while there are
 * OPEN issues relevant to the user (job crew + admins) and/or OPEN tasks
 * assigned to the user. It is draggable (position persisted per user), can be
 * dismissed by dropping it on the X target at the bottom, and tapping it opens
 * a cartoon speech balloon listing the items with quick actions:
 * issues → "View in chat" / "Resolve" · tasks → "View in chat" / "Complete".
 */

const BTN = 56; // floating button size (px)
const EDGE = 10; // min distance from viewport edges

const clampPos = (x, y) => ({
  x: Math.min(Math.max(x, EDGE), window.innerWidth - BTN - EDGE),
  y: Math.min(Math.max(y, EDGE + 40), window.innerHeight - BTN - EDGE - 60),
});

const timeAgo = (iso, t) => {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return t("fi_now");
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

const PRIORITY_DOT = {
  high: "bg-destructive",
  medium: "bg-amber-500",
  low: "bg-muted-foreground/50",
};

export const FloatingIssueAlert = () => {
  const { user, t } = useApp();
  const navigate = useNavigate();
  const [issues, setIssues] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [open, setOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [resolveFor, setResolveFor] = useState(null); // issue being resolved
  const [pos, setPos] = useState(null); // {x, y}
  const [dragging, setDragging] = useState(false);
  const [overDismiss, setOverDismiss] = useState(false);
  const dragRef = useRef(null); // {px, py, x, y, moved}
  const posKey = user ? `osc_issue_float_pos_v1_${user.id}` : null;

  // ---------- position: restore + clamp on resize ----------
  useEffect(() => {
    if (!posKey) return;
    let start = { x: window.innerWidth - BTN - EDGE, y: Math.round(window.innerHeight * 0.55) };
    try {
      const saved = JSON.parse(localStorage.getItem(posKey) || "null");
      if (saved && Number.isFinite(saved.x) && Number.isFinite(saved.y)) start = saved;
    } catch (e) { /* ignore */ }
    setPos(clampPos(start.x, start.y));
    const onResize = () => setPos((p) => (p ? clampPos(p.x, p.y) : p));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [posKey]);

  // ---------- data ----------
  const refresh = useCallback(async () => {
    if (!user?.id) return;
    try {
      const r = await api.get(`/issues/floating?user_id=${user.id}`);
      setIssues(r.data?.issues || []);
      setTasks(r.data?.tasks || []);
    } catch (e) { /* silent */ }
  }, [user?.id]);

  useEffect(() => {
    refresh();
    const iv = setInterval(refresh, 90000);
    return () => clearInterval(iv);
  }, [refresh]);

  // Live refresh: Shell re-dispatches user-channel WS payloads as a window event
  useEffect(() => {
    const onNotify = (e) => {
      const p = e.detail || {};
      if (p.inner === "issue_alert") refresh();
    };
    window.addEventListener("osc-user-notify", onNotify);
    return () => window.removeEventListener("osc-user-notify", onNotify);
  }, [refresh]);

  // Opened from a push notification → balloon opens automatically
  useEffect(() => {
    const KEY = "osc_open_issue_balloon";
    const check = () => {
      try {
        if (localStorage.getItem(KEY) === "1") {
          localStorage.removeItem(KEY);
          setOpen(true);
        }
      } catch (e) { /* ignore */ }
    };
    check();
    const onVis = () => { if (!document.hidden) check(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // ---------- drag / tap / dismiss ----------
  const dismissTarget = () => ({ x: window.innerWidth / 2, y: window.innerHeight - 80 });

  const onPointerDown = (e) => {
    if (!pos) return;
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch (err) { /* pointer already gone */ }
    dragRef.current = { px: e.clientX, py: e.clientY, x: pos.x, y: pos.y, moved: false };
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.px;
    const dy = e.clientY - d.py;
    if (!d.moved && Math.abs(dx) + Math.abs(dy) > 7) {
      d.moved = true;
      setDragging(true);
      setOpen(false);
    }
    if (d.moved) {
      setPos(clampPos(d.x + dx, d.y + dy));
      const tgt = dismissTarget();
      setOverDismiss(Math.hypot(e.clientX - tgt.x, e.clientY - tgt.y) < 56);
    }
  };

  const onPointerUp = () => {
    const d = dragRef.current;
    dragRef.current = null;
    setDragging(false);
    if (d?.moved) {
      if (overDismiss) {
        setOverDismiss(false);
        dismissAll();
        return;
      }
      setOverDismiss(false);
      setPos((p) => {
        if (p && posKey) {
          try { localStorage.setItem(posKey, JSON.stringify(p)); } catch (e) { /* ignore */ }
        }
        return p;
      });
    } else {
      setOpen((o) => !o);
      setExpandedId(null);
    }
  };

  const dismissAll = async () => {
    const iss = [...issues];
    const tks = [...tasks];
    setIssues([]);
    setTasks([]);
    setOpen(false);
    try {
      await Promise.all([
        ...iss.map((i) => api.post(`/issues/${i.id}/dismiss`, { user_id: user.id })),
        ...tks.map((tk) => api.post(`/tasks/${tk.id}/dismiss`, { user_id: user.id })),
      ]);
      toast.info(t("fi_dismissed"));
    } catch (e) {
      refresh();
    }
  };

  // ---------- actions ----------
  const viewInChat = (it) => {
    setOpen(false);
    if (it.conversation_id) {
      navigate(`/chat/${it.conversation_id}${it.message_id ? `?msg=${it.message_id}` : ""}`);
    }
  };

  const startResolve = async (it) => {
    if (it.conversation_id) {
      setResolveFor(it);
    } else {
      // No source conversation: resolve directly
      try {
        await api.patch(`/issues/${it.id}`, { status: "resolved", user_id: user.id });
        toast.success(`${t("issue")}: ${t("status_resolved")}`);
        refresh();
      } catch (e) {
        toast.error("Failed");
      }
    }
  };

  const completeTask = async (tk) => {
    try {
      await api.patch(`/tasks/${tk.id}`, { status: "done", user_id: user.id });
      toast.success(t("fi_task_done"));
      setTasks((prev) => prev.filter((x) => x.id !== tk.id));
      refresh();
    } catch (e) {
      toast.error("Failed");
    }
  };

  // Synthetic message/conversation so ResolveIssueDialog can post the
  // resolution into the issue's source conversation (same field flow).
  const resolveMessage = useMemo(() => (resolveFor ? {
    id: resolveFor.message_id,
    sender_id: resolveFor.created_by,
    content: resolveFor.title,
    meta: { issue_id: resolveFor.id, title: resolveFor.title, linked_job_id: resolveFor.job_id },
  } : null), [resolveFor]);
  const resolveConversation = useMemo(() => (resolveFor ? {
    id: resolveFor.conversation_id,
    job_id: null,
    participants: [{ id: resolveFor.created_by, name: resolveFor.created_by_name }],
  } : null), [resolveFor]);

  if (!user || !pos || (issues.length === 0 && tasks.length === 0)) return null;

  const total = issues.length + tasks.length;
  const issuesOnly = issues.length > 0;
  const headerTitle = issues.length && tasks.length
    ? t("fi_alerts_title")
    : issues.length ? t("fi_issues_title") : t("fi_tasks_title");

  // ---------- balloon geometry (cartoon speech bubble) ----------
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const bw = Math.min(330, vw - 20);
  const bLeft = Math.min(Math.max(pos.x + BTN / 2 - bw / 2, 10), vw - bw - 10);
  const below = pos.y < vh * 0.5; // balloon under the button when it sits high
  const tailX = Math.min(Math.max(pos.x + BTN / 2 - bLeft - 9, 18), bw - 30);

  return (
    <>
      {/* Floating pulsing alert button: ⚠️ when there are issues, 📋 for tasks only */}
      <button
        data-testid="floating-issue-btn"
        aria-label={headerTitle}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        style={{ left: pos.x, top: pos.y, width: BTN, height: BTN, touchAction: "none" }}
        className={`fixed z-[45] rounded-full text-white shadow-xl border-2 border-white/70
          flex items-center justify-center select-none cursor-grab active:cursor-grabbing
          ${issuesOnly ? "bg-destructive" : "bg-primary"}
          ${dragging ? "scale-110" : open ? "" : "osc-issue-pulse"}`}
      >
        {issuesOnly
          ? <AlertTriangle className="h-7 w-7" strokeWidth={2.4} />
          : <ClipboardList className="h-7 w-7" strokeWidth={2.3} />}
        <span
          data-testid="floating-issue-badge"
          className={`absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-background
            border text-[11px] font-bold flex items-center justify-center
            ${issuesOnly ? "text-destructive border-destructive" : "text-primary border-primary"}`}
        >
          {total}
        </span>
      </button>

      {/* Drop-to-dismiss target (Messenger style) */}
      {dragging && (
        <div
          data-testid="floating-issue-dismiss-target"
          style={{ left: "50%", bottom: 44, transform: "translateX(-50%)" }}
          className={`fixed z-[44] h-16 w-16 rounded-full border-2 border-dashed flex items-center justify-center
            transition-opacity duration-150
            ${overDismiss ? "bg-destructive text-white border-destructive scale-110" : "bg-background/90 text-muted-foreground border-muted-foreground/50"}`}
        >
          <X className="h-7 w-7" />
        </div>
      )}
      {dragging && (
        <div className="fixed z-[44] left-1/2 -translate-x-1/2 bottom-7 text-[10px] text-muted-foreground bg-background/90 rounded-full px-2 py-0.5 border">
          {t("fi_drag_hint")}
        </div>
      )}

      {/* Cartoon speech balloon */}
      {open && !dragging && (
        <div
          data-testid="floating-issue-balloon"
          style={{
            left: bLeft,
            width: bw,
            ...(below ? { top: pos.y + BTN + 14 } : { bottom: vh - pos.y + 14 }),
          }}
          className="fixed z-[46] rounded-2xl border-2 border-border bg-card shadow-2xl"
        >
          {/* Balloon tail pointing at the button */}
          <div
            style={{ left: tailX, [below ? "top" : "bottom"]: -9 }}
            className={`absolute h-4 w-4 rotate-45 bg-card ${below ? "border-l-2 border-t-2" : "border-r-2 border-b-2"} border-border`}
          />
          <div className="flex items-center gap-2 px-3 pt-2.5 pb-2 border-b">
            {issuesOnly
              ? <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
              : <ClipboardList className="h-4 w-4 text-primary shrink-0" />}
            <div className="text-sm font-semibold flex-1" data-testid="floating-issue-balloon-title">
              {headerTitle} ({total})
            </div>
            <button
              data-testid="floating-issue-balloon-close"
              onClick={() => setOpen(false)}
              className="h-7 w-7 flex items-center justify-center rounded-full hover:bg-muted"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="max-h-[46vh] overflow-y-auto py-1">
            {issues.length > 0 && tasks.length > 0 && (
              <div className="px-4 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-destructive flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" /> {t("fi_issues_title")} ({issues.length})
              </div>
            )}
            {issues.map((it) => {
              const expanded = expandedId === it.id;
              return (
                <div key={it.id} className="px-2 py-0.5">
                  <button
                    data-testid="floating-issue-item"
                    onClick={() => setExpandedId(expanded ? null : it.id)}
                    className={`w-full text-left rounded-xl px-2.5 py-2 transition-colors ${expanded ? "bg-muted" : "hover:bg-muted/60"}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${PRIORITY_DOT[it.priority] || PRIORITY_DOT.medium}`} />
                      <span className="text-sm font-medium flex-1 truncate">{it.title}</span>
                      <span className="text-[10px] text-muted-foreground shrink-0">{timeAgo(it.created_at, t)}</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 ml-[18px] truncate">
                      {it.job_number ? `#${it.job_number} — ${it.customer_name}` : it.customer_name}
                      {it.created_by_name ? ` · ${t("fi_by")} ${it.created_by_name}` : ""}
                    </div>
                  </button>
                  {expanded && (
                    <div className="ml-[18px] mr-1 mb-1.5 mt-0.5 space-y-1.5" data-testid="floating-issue-details">
                      {it.description && it.description !== it.title && (
                        <div className="text-xs text-foreground/80">{it.description}</div>
                      )}
                      {it.location && (
                        <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                          <MapPin className="h-3 w-3" /> {it.location}
                        </div>
                      )}
                      <div className="flex gap-1.5 pt-0.5">
                        {it.conversation_id && (
                          <Button
                            data-testid="floating-issue-view-chat"
                            size="sm"
                            variant="outline"
                            className="h-8 text-xs flex-1"
                            onClick={() => viewInChat(it)}
                          >
                            <MessageSquare className="h-3.5 w-3.5 mr-1" /> {t("fi_view_chat")}
                          </Button>
                        )}
                        <Button
                          data-testid="floating-issue-resolve"
                          size="sm"
                          className="h-8 text-xs flex-1 bg-[hsl(var(--success,142_70%_35%))] hover:opacity-90 text-white"
                          onClick={() => startResolve(it)}
                        >
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> {t("fi_resolve")}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {tasks.length > 0 && issues.length > 0 && (
              <div className="px-4 pt-1.5 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary flex items-center gap-1 border-t mt-1">
                <ClipboardList className="h-3 w-3" /> {t("fi_tasks_title")} ({tasks.length})
              </div>
            )}
            {tasks.map((tk) => {
              const expanded = expandedId === tk.id;
              return (
                <div key={tk.id} className="px-2 py-0.5">
                  <button
                    data-testid="floating-task-item"
                    onClick={() => setExpandedId(expanded ? null : tk.id)}
                    className={`w-full text-left rounded-xl px-2.5 py-2 transition-colors ${expanded ? "bg-muted" : "hover:bg-muted/60"}`}
                  >
                    <div className="flex items-center gap-2">
                      <ClipboardList className="h-3.5 w-3.5 text-primary shrink-0" />
                      <span className="text-sm font-medium flex-1 truncate">{tk.title}</span>
                      <span className="text-[10px] text-muted-foreground shrink-0">{timeAgo(tk.created_at, t)}</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 ml-[22px] truncate">
                      {tk.job_number ? `#${tk.job_number} — ${tk.customer_name}` : tk.customer_name}
                      {tk.created_by_name ? ` · ${t("fi_by")} ${tk.created_by_name}` : ""}
                    </div>
                  </button>
                  {expanded && (
                    <div className="ml-[22px] mr-1 mb-1.5 mt-0.5 space-y-1.5" data-testid="floating-task-details">
                      {tk.description && tk.description !== tk.title && (
                        <div className="text-xs text-foreground/80">{tk.description}</div>
                      )}
                      {tk.due_label && (
                        <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" /> {tk.due_label}
                        </div>
                      )}
                      <div className="flex gap-1.5 pt-0.5">
                        {tk.conversation_id && (
                          <Button
                            data-testid="floating-task-view-chat"
                            size="sm"
                            variant="outline"
                            className="h-8 text-xs flex-1"
                            onClick={() => viewInChat(tk)}
                          >
                            <MessageSquare className="h-3.5 w-3.5 mr-1" /> {t("fi_view_chat")}
                          </Button>
                        )}
                        <Button
                          data-testid="floating-task-complete"
                          size="sm"
                          className="h-8 text-xs flex-1 bg-[hsl(var(--success,142_70%_35%))] hover:opacity-90 text-white"
                          onClick={() => completeTask(tk)}
                        >
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> {t("fi_complete")}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Field resolve flow (same dialog used inside the chat) */}
      {resolveFor && (
        <ResolveIssueDialog
          open={!!resolveFor}
          onClose={() => setResolveFor(null)}
          message={resolveMessage}
          conversation={resolveConversation}
          onResolved={() => { setResolveFor(null); refresh(); }}
        />
      )}
    </>
  );
};
